import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { PartnerService } from '@feature/dashboard/partners/services/partner.service';
import { PartnerListInterface } from '@feature/dashboard/partners/types/partner-list';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { sortDirection } from '@sharedModule/constants/data-grid.constants';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { HasPlatformAccessDirective } from '@sharedModule/directives/has-platform-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ConfirmationCommonDialogData } from '@sharedModule/models/confirmation-dialog.model';
import { ConfirmationDialogService } from '@sharedModule/services/confirmation-dialog/confirmation-dialog.service';
import { getActiveSort } from '@sharedModule/utils/sort.utils';
import { Subject, Observable, debounceTime, takeUntil } from 'rxjs';
import { UserService } from '../../services/user.service';
import {
  updateUserListFilter,
  deleteUser,
} from '../../store/user-list.actions';
import { selectUserList } from '../../store/user-list.selector';
import { UserListInterface } from '../../types/user-list';
import {
  defaultUserListFilter,
  UserListFilter,
} from '../../types/user-list-filter';

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    HasAccessDirective,
    ReactiveFormsModule,
    HasPlatformAccessDirective,
  ],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss',
})
export class UserListComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.users;
  filterForm!: FormGroup;
  displayedColumns: string[] = [
    'first_name',
    'email',
    'role_type',
    'role_name',
    'action',
  ];
  dataSource!: MatTableDataSource<UserListInterface>;
  activeSort?: Sort;
  fallBackSort = {
    active: 'created_at',
    direction: sortDirection.desc,
  };
  page = 0;
  total = 0;
  limit = 10;
  subject = new Subject<{
    partner_id: number;
    search: string;
    role: string;
  }>();
  isSuperAdmin!: boolean;
  partnerList!: Observable<PartnerListInterface[]>;
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private userService: UserService,
    private partnerService: PartnerService,
    private authenticationService: AuthenticationService,
    private messageHandlerService: MessageHandlerService,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    this.isSuperAdmin = this.authenticationService.isSuperAdmin();
  }

  ngOnInit(): void {
    this.filterFormInit();
    this.loadData();
    this.dispatchFilter();
    const debounceTimeDuration = 1000;
    this.subject
      .pipe(debounceTime(debounceTimeDuration), takeUntil(this.#destroy$))
      .subscribe(res => {
        const partnerId = this.filterForm.value[this.attrConstant.partner.key];
        this.dispatchFilter({
          count: 10,
          page: 0,
          ...res,
          partner_id: ['', null].includes(partnerId) ? null : [partnerId],
        });
      });
    if (this.isSuperAdmin) this.partnerList = this.partnerService.sharedData$;
  }

  filterFormInit(): void {
    this.filterForm = new FormGroup({
      [this.attrConstant.partner.key]: new FormControl(''),
      [this.attrConstant.search.key]: new FormControl(''),
      [this.attrConstant.role.key]: new FormControl(''),
    });
  }

  loadData(): void {
    this.store
      .select(selectUserList)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) {
          this.dataSource = new MatTableDataSource(data.rawData);
          this.page = data.page;
          this.total = data.totalRecords;
          this.limit = data.count;
        }
      });
  }

  applyFilter(): void {
    this.subject.next(this.filterForm.value);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.dispatchFilter({ ...defaultUserListFilter });
  }

  onPageChange(pageEvent: PageEvent): void {
    this.dispatchFilter({
      count: pageEvent.pageSize,
      page: pageEvent.pageIndex,
    });
  }

  onSortChange(sortEvent: Sort): void {
    this.activeSort = getActiveSort(sortEvent, this.fallBackSort);
    this.dispatchFilter({
      sort_key: this.activeSort.active,
      sort_by: this.activeSort.direction,
    });
  }

  dispatchFilter(userListFilter?: UserListFilter): void {
    this.store.dispatch(updateUserListFilter({ filter: userListFilter ?? {} }));
  }

  async onDeleteUser(userId: string, partnerId: number): Promise<void> {
    const alertCommonDialogData: ConfirmationCommonDialogData = {
      title: '',
      message: `Are you sure you're going to delete this user?`,
      submitButtonText: 'Confirm',
    };
    const isConfirmed =
      await this.confirmationDialogService.openConfirmationDialog(
        alertCommonDialogData
      );
    if (isConfirmed) {
      this.userService
        .deleteUser({
          user_id: userId,
          partner_id: partnerId,
        })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) {
              this.messageHandlerService.show(x.message);
              this.store.dispatch(deleteUser({ user_id: userId }));
            }
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
